import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import {
  Clock,
  MapPin,
  Camera,
  MessageSquare,
  Users,
  FileText,
  Shield,
  Smartphone,
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Phone,
  Mail,
  Building2,
  Handshake,
  ChevronDown,
  Zap,
  BarChart3,
  Bell,
  Target,
  Map,
  AlertTriangle,
  Settings,
  FileSearch,
  Radio,
  WifiOff,
  Wifi,
  TrendingUp,
  Award,
  Lock,
  Download,
  Database,
  Eye,
  ScanFace,
  ShieldCheck,
  UserCheck,
  Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function HeroMediaTransition() {
  const [phase, setPhase] = useState("image"); // "image" | "fading" | "video"
  const videoRef = useRef(null);

  useEffect(() => {
    const fadeTimer = setTimeout(() => setPhase("fading"), 3000);
    const videoTimer = setTimeout(() => setPhase("video"), 4200);
    return () => { clearTimeout(fadeTimer); clearTimeout(videoTimer); };
  }, []);

  useEffect(() => {
    if (phase === "video" && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(() => {});
    }
  }, [phase]);

  return (
    <div className="relative animate-fade-in">
      <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-white" style={{ aspectRatio: "16/10" }}>
        {/* Biometric Image - fades out */}
        <img
          src="https://customer-assets.emergentagent.com/job_ponto-whatsapp/artifacts/n25ka9tx_arte2-pontobiometrico.webp"
          alt="Ponto Biométrico - CiaPonto"
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: phase === "image" ? 1 : 0,
            transition: "opacity 1.2s ease-in-out",
            zIndex: 2,
            pointerEvents: phase === "video" ? "none" : "auto",
          }}
        />
        {/* Video - logo reveal animation */}
        <video
          ref={videoRef}
          preload="auto"
          autoPlay
          muted
          playsInline
          loop
          className="absolute inset-0 w-full h-full object-contain"
          style={{ zIndex: 1, background: "white" }}
        >
          <source src="/ciaponto-intro.webm" type="video/webm" />
          <source src="/ciaponto-intro-web.mp4" type="video/mp4" />
        </video>
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visitorStats, setVisitorStats] = useState({ total: 0, today: 0 });
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    employees_count: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  // Rastrear visitante ao carregar a página
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        console.log("[Visitantes] Iniciando tracking...");
        const response = await axios.post(`${API}/visitors/track`);
        console.log("[Visitantes] Resposta:", response.data);
        setVisitorStats({
          total: response.data.total_visitors,
          today: response.data.today_visitors
        });
      } catch (error) {
        console.error("[Visitantes] Erro ao rastrear:", error);
        // Tentar buscar apenas as estatísticas se o tracking falhar
        try {
          const statsResponse = await axios.get(`${API}/visitors/stats`);
          setVisitorStats({
            total: statsResponse.data.total,
            today: statsResponse.data.today
          });
        } catch (statsError) {
          console.error("[Visitantes] Erro ao buscar stats:", statsError);
        }
      }
    };
    trackVisitor();
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, contactForm);
      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setContactForm({ name: "", email: "", phone: "", company: "", employees_count: "", message: "" });
    } catch (error) {
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setSubmitting(false);
    }
  };

  const features = [
    {
      icon: BarChart3,
      title: "Dashboards Gerenciais",
      description: "Dados em tempo real para acompanhar assiduidade, ponto e horas extras de funcionários, estagiários e terceiros."
    },
    {
      icon: Clock,
      title: "Cálculo de Hora Extra",
      description: "Cálculo automático de hora extra no início e fim da jornada, com controle total."
    },
    {
      icon: TrendingUp,
      title: "Banco de Horas Automatizado",
      description: "Controle de banco de horas automatizado e flexível, sem planilhas manuais."
    },
    {
      icon: Target,
      title: "Cerca Virtual (Perímetro)",
      description: "Defina o perímetro de marcação de ponto. Funcionário só registra dentro da área autorizada."
    },
    {
      icon: Shield,
      title: "Comprovante Digital",
      description: "Comprovante digital com assinatura eletrônica, foto e localização GPS como prova irrefutável."
    },
    {
      icon: MapPin,
      title: "Geolocalização Precisa",
      description: "Registro com geolocalização precisa para comprovação do local de trabalho."
    }
  ];

  const plans = [
    {
      name: "Básico",
      price: "R$ 159",
      period: "/mês",
      description: "Ideal para pequenas equipes",
      features: [
        "Até 5 funcionários",
        "Registro de ponto Aplicativo ou Web",
        "Foto comprobatória obrigatória",
        "Cerca Eletrônica (Anti-Fraude)",
        "Registro Offline (sincroniza depois)",
        "Relatórios gerenciais completos",
        "Banco de horas automático",
        "Cálculo de horas extras",
        "Adicional noturno",
        "Biometria Facial Avançada",
        "Dashboards gerenciais em tempo real",
        "Assinatura de Folha",
        "App nas lojas oficiais",
        "App para uso coletivo em tablet",
        "Chat com profissionais"
      ],
      highlighted: false
    },
    {
      name: "Profissional",
      price: "R$ 299",
      period: "/mês",
      description: "Para empresas em crescimento",
      features: [
        "Até 10 funcionários",
        "Registro de ponto Aplicativo ou Web",
        "Foto comprobatória obrigatória",
        "Cerca Eletrônica (Anti-Fraude)",
        "Registro Offline (sincroniza depois)",
        "Relatórios gerenciais completos",
        "Banco de horas automático",
        "Cálculo de horas extras",
        "Adicional noturno",
        "Biometria Facial Avançada",
        "Dashboards gerenciais em tempo real",
        "Assinatura de Folha",
        "App nas lojas oficiais",
        "App para uso coletivo em tablet",
        "Chat com profissionais"
      ],
      highlighted: true
    },
    {
      name: "Enterprise",
      price: "R$ 499",
      period: "/mês",
      description: "Solução completa para grandes empresas",
      features: [
        "Até 30 funcionários",
        "Registro de ponto Aplicativo ou Web",
        "Foto comprobatória obrigatória",
        "Cerca Eletrônica (Anti-Fraude)",
        "Registro Offline (sincroniza depois)",
        "Relatórios gerenciais completos",
        "Banco de horas automático",
        "Cálculo de horas extras",
        "Adicional noturno",
        "Biometria Facial Avançada",
        "Dashboards gerenciais em tempo real",
        "Assinatura de Folha",
        "App nas lojas oficiais",
        "App para uso coletivo em tablet",
        "Chat com profissionais",
        "Gerente de conta dedicado"
      ],
      highlighted: false
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <img 
                src="/logo-ciaponto-color.png" 
                alt="CiaPonto" 
                className="h-10 w-auto"
              />
            </div>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Funcionalidades</a>
              <a href="#biometria" className="text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                <ScanFace className="w-4 h-4 text-[#1CA1BA]" />
                Biometria Facial
              </a>
              <a href="#geofencing" className="text-slate-600 hover:text-slate-900 transition-colors flex items-center gap-1">
                <Target className="w-4 h-4 text-orange-500" />
                Cerca Eletrônica
              </a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Preços</a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 transition-colors">Contato</a>
            </nav>

            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" data-testid="header-login-btn">Entrar</Button>
              </Link>
              <a href="https://ciaponto.vercel.app/registration" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#1CA1BA] hover:bg-[#17889E]" data-testid="header-register-btn">
                  Começar Grátis
                </Button>
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Nav */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-slate-200">
              <nav className="flex flex-col gap-4">
                <a href="#features" className="text-slate-600 hover:text-slate-900">Funcionalidades</a>
                <a href="#biometria" className="text-slate-600 hover:text-slate-900 flex items-center gap-2">
                  <ScanFace className="w-4 h-4 text-[#1CA1BA]" />
                  Biometria Facial
                </a>
                <a href="#geofencing" className="text-slate-600 hover:text-slate-900 flex items-center gap-2">
                  <Target className="w-4 h-4 text-orange-500" />
                  Cerca Eletrônica
                </a>
                <a href="#pricing" className="text-slate-600 hover:text-slate-900">Preços</a>
                <a href="#contact" className="text-slate-600 hover:text-slate-900">Contato</a>
                <Link to="/login">
                  <Button variant="ghost" className="w-full justify-start">Entrar</Button>
                </Link>
                <a href="https://ciaponto.vercel.app/registration" target="_blank" rel="noopener noreferrer">
                  <Button className="w-full bg-[#1CA1BA] hover:bg-[#17889E]">Começar Grátis</Button>
                </a>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-up">
              <div className="inline-flex items-center gap-2 bg-cyan-100 text-[#137082] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Sistema de Ponto Eletrônico Inteligente
              </div>
              <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight mb-6">
                Controle de Ponto
                <span className="text-[#1CA1BA]"> com Prova Digital</span>
              </h1>
              <p className="text-lg text-slate-600 mb-8 max-w-xl">
                Gerencie a jornada de trabalho da sua equipe com geolocalização GPS 
                e fotos comprobatórias. Prova digital irrefutável de cada registro.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://ciaponto.vercel.app/registration" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-[#1CA1BA] hover:bg-[#17889E] h-12 px-8 text-base" data-testid="hero-cta-btn">
                    Começar Teste Grátis
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <a href="#features">
                  <Button size="lg" variant="outline" className="h-12 px-8 text-base" data-testid="hero-features-btn">
                    Ver Funcionalidades
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-slate-600">7 dias grátis</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-slate-600">Sem cartão de crédito</span>
                </div>
              </div>
            </div>

            <HeroMediaTransition />
          </div>
        </div>
      </section>

      {/* Compliance Section - Portaria 671/2021 */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 bg-[#1CA1BA]/20 text-cyan-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Award className="w-5 h-5" />
              Conformidade Legal Garantida
            </div>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-white mb-4">
              ✅ 100% Em Conformidade com a Portaria 671/2021
            </h2>
            <p className="text-slate-300 max-w-3xl mx-auto">
              O CiaPonto é um sistema de Registro Eletrônico de Ponto (REP-P) totalmente adequado às exigências 
              da Portaria 671/2021 do Ministério do Trabalho e Emprego.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 hover:border-[#1CA1BA]/50 transition-colors">
              <div className="w-12 h-12 bg-[#1CA1BA]/20 rounded-lg flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Comprovante Digital</h3>
              <p className="text-slate-400 text-sm">
                Emissão automática após cada registro de ponto, disponível para download imediato.
              </p>
            </div>

            <div className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 hover:border-[#1CA1BA]/50 transition-colors">
              <div className="w-12 h-12 bg-[#1CA1BA]/20 rounded-lg flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Carimbo de Tempo Criptografado</h3>
              <p className="text-slate-400 text-sm">
                Garantia de integridade e autenticidade de todos os registros de ponto.
              </p>
            </div>

            <div className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 hover:border-[#1CA1BA]/50 transition-colors">
              <div className="w-12 h-12 bg-[#1CA1BA]/20 rounded-lg flex items-center justify-center mb-4">
                <Database className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">AFD (Arquivo Fonte de Dados)</h3>
              <p className="text-slate-400 text-sm">
                Geração e exportação no formato oficial exigido para fiscalização trabalhista.
              </p>
            </div>

            <div className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 hover:border-[#1CA1BA]/50 transition-colors">
              <div className="w-12 h-12 bg-[#1CA1BA]/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Disponibilização Imediata</h3>
              <p className="text-slate-400 text-sm">
                Dados acessíveis instantaneamente para Auditores-Fiscais do Trabalho.
              </p>
            </div>

            <div className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 hover:border-[#1CA1BA]/50 transition-colors">
              <div className="w-12 h-12 bg-[#1CA1BA]/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">Registros Fidedignos</h3>
              <p className="text-slate-400 text-sm">
                Entradas, saídas e intervalos registrados com precisão e inviolabilidade.
              </p>
            </div>

            <div className="bg-slate-700/50 backdrop-blur-sm rounded-xl p-6 border border-slate-600/50 hover:border-[#1CA1BA]/50 transition-colors">
              <div className="w-12 h-12 bg-[#1CA1BA]/20 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="w-6 h-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white text-lg mb-2">REP-P Homologado</h3>
              <p className="text-slate-400 text-sm">
                Sistema registrador via programa em total conformidade com a legislação vigente.
              </p>
            </div>
          </div>

          <div className="text-center mt-10">
            <p className="text-slate-400 text-sm italic">
              Sua empresa em dia com a legislação trabalhista.
            </p>
          </div>
        </div>
      </section>

      {/* Biometria Facial Section - NOVO DESTAQUE */}
      <section id="biometria" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#1CA1BA] via-[#17889E] to-[#137082] relative overflow-hidden">
        {/* Background decorativo */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#1CA1BA] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Conteúdo */}
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-white/30">
                <Sparkles className="w-4 h-4" />
                NOVA FUNCIONALIDADE
              </div>
              
              <h2 className="font-heading font-black text-4xl sm:text-5xl leading-tight mb-6">
                Reconhecimento Facial
                <span className="block text-cyan-200">com Inteligência Artificial</span>
              </h2>
              
              <p className="text-lg text-cyan-100 mb-8 leading-relaxed">
                Tecnologia de ponta usando <strong>AWS Rekognition</strong> para garantir que apenas o 
                funcionário cadastrado possa registrar seu ponto. Elimine fraudes e garanta 
                a autenticidade de cada marcação.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Anti-Fraude Avançado</h4>
                    <p className="text-cyan-200 text-sm">Impossível bater ponto por outra pessoa. O sistema compara a foto em tempo real com o cadastro.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <UserCheck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Verificação Instantânea</h4>
                    <p className="text-cyan-200 text-sm">Resultado em menos de 2 segundos. Rápido, preciso e sem fricção para o colaborador.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-white">Segurança de Dados</h4>
                    <p className="text-cyan-200 text-sm">Dados biométricos processados com criptografia e em conformidade com a LGPD.</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="https://ciaponto.vercel.app/registration" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-white text-[#137082] hover:bg-cyan-50 h-12 px-8 text-base font-semibold shadow-lg" data-testid="biometria-cta-btn">
                    <ScanFace className="w-5 h-5 mr-2" />
                    Ativar Biometria Agora
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Visual/Ilustração */}
            <div className="relative">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20 shadow-2xl">
                {/* Ícone central grande */}
                <div className="flex justify-center mb-6">
                  <div className="w-32 h-32 bg-gradient-to-br from-cyan-400 to-cyan-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                    <ScanFace className="w-16 h-16 text-white" />
                  </div>
                </div>
                
                {/* Status de verificação */}
                <div className="bg-white/20 backdrop-blur rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-semibold">Identidade Verificada</p>
                      <p className="text-cyan-200 text-sm">Similaridade: 98.7%</p>
                    </div>
                  </div>
                </div>
                
                {/* Métricas */}
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-2xl font-bold text-white">99.9%</p>
                    <p className="text-cyan-200 text-xs">Precisão</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-2xl font-bold text-white">&lt;2s</p>
                    <p className="text-cyan-200 text-xs">Tempo</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <p className="text-2xl font-bold text-white">0%</p>
                    <p className="text-cyan-200 text-xs">Fraudes</p>
                  </div>
                </div>
              </div>
              
              {/* Badge flutuante */}
              <div className="absolute -top-4 -right-4 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg animate-bounce">
                🔥 EXCLUSIVO
              </div>
            </div>
          </div>
          
          {/* Logos de tecnologia */}
          <div className="mt-16 text-center">
            <p className="text-cyan-200 text-sm mb-4">Tecnologia de reconhecimento facial powered by</p>
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-lg px-6 py-3">
              <span className="text-white font-semibold">Amazon Web Services</span>
              <span className="text-cyan-300">|</span>
              <span className="text-cyan-200">AWS Rekognition</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Funcionalidades que Impulsionam a Gestão do Ponto
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Relatórios gerenciais completos para acompanhar assiduidade, ponto e horas extras de funcionários, estagiários e terceiros.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="border border-slate-200 hover:border-cyan-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg group"
                data-testid={`feature-card-${index}`}
              >
                <CardHeader>
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#1CA1BA] transition-colors">
                    <feature.icon className="w-6 h-6 text-[#17889E] group-hover:text-white transition-colors" />
                  </div>
                  <CardTitle className="font-heading text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mb-4">
              Como Funciona
            </h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Simples, rápido e seguro. Veja como é fácil usar o CiaPonto.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1CA1BA] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="font-heading font-bold text-2xl">1</span>
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Cadastre sua Equipe</h3>
              <p className="text-slate-400">
                Adicione seus funcionários com nome, email e telefone para acesso ao sistema.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1CA1BA] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="font-heading font-bold text-2xl">2</span>
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Registre o Ponto</h3>
              <p className="text-slate-400">
                Funcionários registram entrada e saída pelo sistema web com foto e localização GPS obrigatórios.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-[#1CA1BA] rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="font-heading font-bold text-2xl">3</span>
              </div>
              <h3 className="font-heading font-bold text-xl mb-3">Acompanhe em Tempo Real</h3>
              <p className="text-slate-400">
                Visualize todos os registros, gere relatórios e controle o banco de horas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Cerca Eletrônica Section */}
      <section id="geofencing" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-cyan-50 via-white to-orange-50 relative overflow-hidden">
        {/* Background decorativo */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-200/30 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="max-w-7xl mx-auto relative">
          {/* Header da seção */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              Exclusivo Planos Profissional e Enterprise
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Cerca Eletrônica <span className="text-orange-500">(Anti-Fraude)</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto">
              Delimite áreas geográficas autorizadas para registro de ponto. 
              Se um colaborador registrar fora da área definida, <strong>você fica sabendo na hora!</strong>
            </p>
          </div>

          {/* Cards principais */}
          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {/* Card Como Definir */}
            <Card className="border-2 border-cyan-200 bg-white/80 backdrop-blur shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="font-heading text-2xl text-slate-900">Como Funciona?</CardTitle>
                <CardDescription className="text-base">
                  Configure cercas geográficas de forma simples e intuitiva
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Map className="w-5 h-5 text-[#17889E]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Defina um Local</h4>
                    <p className="text-slate-600 text-sm">
                      No cadastro do colaborador, use o mapa interativo para definir um ponto central e um raio. 
                      O colaborador só poderá registrar ponto dentro desta área.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Settings className="w-5 h-5 text-[#17889E]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Gerencie com Flexibilidade</h4>
                    <p className="text-slate-600 text-sm">
                      Defina cercas por grupos ou individualmente. Escolha raios por bairro, região ou cidade. 
                      Altere ou remova a qualquer momento.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-[#17889E]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Múltiplos Locais</h4>
                    <p className="text-slate-600 text-sm">
                      Configure diferentes cercas para equipes externas, técnicos de campo, 
                      vendedores ou qualquer colaborador que trabalhe em locais específicos.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card Como Consultar */}
            <Card className="border-2 border-orange-200 bg-white/80 backdrop-blur shadow-xl hover:shadow-2xl transition-all duration-300">
              <CardHeader className="pb-4">
                <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-500 rounded-xl flex items-center justify-center mb-4 shadow-lg">
                  <AlertTriangle className="w-7 h-7 text-white" />
                </div>
                <CardTitle className="font-heading text-2xl text-slate-900">Como Consultar?</CardTitle>
                <CardDescription className="text-base">
                  Monitore violações e receba alertas em tempo real
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <FileSearch className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Relatórios Detalhados</h4>
                    <p className="text-slate-600 text-sm">
                      Consulte violações por colaborador, grupo ou data. Visualize mapas com a localização exata 
                      e os cálculos de desvio da área autorizada.
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Relatórios Gerenciais</h4>
                    <p className="text-slate-600 text-sm">
                      Relatórios completos para acompanhar assiduidade, ponto e horas extras de 
                      <strong className="text-orange-600"> funcionários, estagiários e terceiros</strong>.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 mb-1">Visualização no Mapa</h4>
                    <p className="text-slate-600 text-sm">
                      Veja exatamente onde cada registro foi feito. Compare com a área autorizada 
                      e identifique padrões de comportamento.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Card Registro Offline */}
          <Card className="border-2 border-slate-200 bg-white/80 backdrop-blur shadow-xl mb-16">
            <CardContent className="p-8">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-cyan-100 text-[#137082] px-4 py-2 rounded-full text-sm font-medium mb-4">
                    <WifiOff className="w-4 h-4" />
                    Funciona Sem Internet
                  </div>
                  <h3 className="font-heading font-bold text-2xl text-slate-900 mb-4">
                    Registro Offline com GPS
                  </h3>
                  <p className="text-slate-600 mb-6">
                    Seus colaboradores podem registrar ponto <strong>mesmo sem conexão com a internet</strong>. 
                    O GPS do dispositivo coleta a localização exata, e quando a conexão for restabelecida, 
                    os dados são sincronizados automaticamente com o sistema.
                  </p>
                  <ul className="space-y-3">
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">GPS funciona offline - localização precisa sempre</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">Sincronização automática ao reconectar</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">Registro com data/hora exata do momento</span>
                    </li>
                    <li className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-slate-700">Ideal para áreas rurais ou sem cobertura</span>
                    </li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6">
                  <div className="bg-white rounded-lg p-4 shadow-lg mb-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                        <WifiOff className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">Modo Offline Ativo</p>
                        <p className="text-sm text-orange-600">2 registros pendentes</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm text-slate-600">Entrada - 08:00 (GPS: -23.5505, -46.6333)</span>
                      </div>
                      <div className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        <span className="text-sm text-slate-600">Saída - 12:00 (GPS: -23.5505, -46.6333)</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-slate-500 flex items-center justify-center gap-2">
                      <Wifi className="w-4 h-4" />
                      Sincronizado automaticamente ao reconectar
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Banner CTA */}
          <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 md:p-12 text-center shadow-2xl">
            <div className="max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-orange-500/20 text-orange-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Proteção Anti-Fraude
              </div>
              <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
                Elimine fraudes de ponto na sua empresa
              </h3>
              <p className="text-slate-300 mb-8 text-lg">
                Com a Cerca Eletrônica, você tem controle total sobre onde seus colaboradores registram ponto. 
                Ideal para equipes externas, técnicos de campo e profissionais que trabalham em múltiplos locais.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="https://ciaponto.vercel.app/registration" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                    <Target className="w-5 h-5 mr-2" />
                    Começar Agora
                  </Button>
                </a>
                <a href="#pricing">
                  <Button size="lg" variant="outline" className="border-slate-500 text-white hover:bg-slate-800 px-8 py-6 text-lg">
                    Ver Planos
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
              Planos e Preços
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Escolha o plano ideal para o tamanho da sua empresa. Teste grátis por 7 dias.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card 
                key={index}
                className={`relative overflow-hidden transition-all duration-300 hover:-translate-y-2 ${
                  plan.highlighted 
                    ? "border-2 border-[#1CA1BA] shadow-xl" 
                    : "border border-slate-200 hover:shadow-lg"
                }`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.highlighted && (
                  <div className="absolute top-0 left-0 right-0 h-1 tracing-beam"></div>
                )}
                <CardHeader className="pb-4">
                  {plan.highlighted && (
                    <span className="inline-block bg-cyan-100 text-[#137082] text-xs font-semibold px-3 py-1 rounded-full mb-4">
                      Mais Popular
                    </span>
                  )}
                  <CardTitle className="font-heading text-2xl">{plan.name}</CardTitle>
                  <CardDescription>{plan.description}</CardDescription>
                  {/* Plan price section */}
                  <div className="mt-4">
                    <span className="font-heading font-black text-4xl text-slate-900">{plan.price}</span>
                    <span className="text-slate-600">{plan.period}</span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <a href="https://ciaponto.vercel.app/registration" target="_blank" rel="noopener noreferrer">
                    <Button 
                      className={`w-full ${plan.highlighted ? "bg-[#1CA1BA] hover:bg-[#17889E]" : ""}`}
                      variant={plan.highlighted ? "default" : "outline"}
                      data-testid={`pricing-btn-${index}`}
                    >
                      Começar Agora
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            <div>
              <h2 className="font-heading font-bold text-3xl sm:text-4xl text-slate-900 mb-4">
                Fale Conosco
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Tem dúvidas sobre o CiaPonto? Entre em contato e nossa equipe responderá em até 24 horas.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Mail className="w-6 h-6 text-[#17889E]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Email</p>
                    <p className="text-slate-600">ciapontosystem@gmail.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-6 h-6 text-[#17889E]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Telefone</p>
                    <p className="text-slate-600">(88) 98867-6142</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-6 h-6 text-[#17889E]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">WhatsApp</p>
                    <p className="text-slate-600">(88) 98867-6142</p>
                  </div>
                </div>
              </div>
            </div>

            <Card className="border border-slate-200">
              <CardHeader>
                <CardTitle className="font-heading">Solicite uma Demonstração</CardTitle>
                <CardDescription>Preencha o formulário e entraremos em contato.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        placeholder="Seu nome"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder="Seu email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Input
                        placeholder="Telefone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        required
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder="Nome da empresa"
                        value={contactForm.company}
                        onChange={(e) => setContactForm({ ...contactForm, company: e.target.value })}
                        required
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>
                  <div>
                    <select
                      className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={contactForm.employees_count}
                      onChange={(e) => setContactForm({ ...contactForm, employees_count: e.target.value })}
                      required
                      data-testid="contact-employees-select"
                    >
                      <option value="">Quantidade de funcionários</option>
                      <option value="1-10">1 a 10 funcionários</option>
                      <option value="11-50">11 a 50 funcionários</option>
                      <option value="51-100">51 a 100 funcionários</option>
                      <option value="100+">Mais de 100 funcionários</option>
                    </select>
                  </div>
                  <div>
                    <textarea
                      className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-background text-sm resize-none"
                      placeholder="Mensagem (opcional)"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      data-testid="contact-message-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#1CA1BA] hover:bg-[#17889E]"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                  >
                    {submitting ? "Enviando..." : "Enviar Mensagem"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1CA1BA] text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <p className="text-white/80 max-w-md">
                Sistema de ponto eletrônico com prova digital. 
                Gerencie a jornada da sua equipe com geolocalização e foto comprobatória.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Links</h4>
              <ul className="space-y-2 text-white/80">
                <li><a href="#features" className="hover:text-white transition-colors">Funcionalidades</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Preços</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contato</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-bold mb-4">Legal</h4>
              <ul className="space-y-2 text-white/80">
                <li><a href="#" className="hover:text-white transition-colors">Termos de Uso</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacidade</a></li>
                <li><a href="#" className="hover:text-white transition-colors">LGPD</a></li>
              </ul>
            </div>
          </div>
          
          {/* Card de Visitantes */}
          <div className="border-t border-white/20 mt-12 pt-8">
            <div className="flex justify-center mb-6">
              <div className="bg-white/15 rounded-2xl px-8 py-4 flex items-center gap-8 shadow-lg backdrop-blur-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{visitorStats.total.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-white/70">Total de visitantes</p>
                  </div>
                </div>
                
                <div className="w-px h-12 bg-white/30"></div>
                
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Eye className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{visitorStats.today.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-white/70">Visitantes hoje</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Logo CiaPonto branca no footer */}
            <div className="flex justify-center mb-4">
              <img 
                src="/logo-ciaponto-white.png" 
                alt="CiaPonto" 
                className="h-12 w-auto"
              />
            </div>
            
            <p className="text-center text-white/80">
              © 2024 CiaPonto - Powered by Ciapay Sistem. Todos os direitos reservados.
            </p>
            <p className="text-center text-white/60 text-sm mt-2">
              Ciapay Sistem LTDA - CNPJ: 64.199.817/0001-13
            </p>
            <p className="text-center text-white/50 text-xs mt-1">
              www.ciaponto.com.br
            </p>
          </div>
        </div>
      </footer>

      {/* Botão Flutuante do WhatsApp */}
      <a
        href="https://wa.me/5588988676142?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20CiaPonto."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 group"
        aria-label="Fale conosco pelo WhatsApp"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 24 24" 
          fill="currentColor" 
          className="w-7 h-7"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        
        {/* Tooltip */}
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-sm px-3 py-2 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Fale conosco! 💬
        </span>
      </a>
    </div>
  );
}
